import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiService } from "../services/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

// Verbindliches Einreichen bleibt gesperrt, bis die Request-Bodies (musikfolgen-list, einreichen-...)
// aus einer Portal-Session mitgeschnitten sind. Das Backend antwortet bis dahin mit 423.
const GEMA_SETLIST_SUBMIT_ENABLED = false;
const GEMA_PORTAL_URL = "https://portal.gema.de/app/einzellizenzen";

let rowCounter = 0;
const nextRowId = () => {
    rowCounter += 1;
    return `row_${rowCounter}`;
};

const emptyRow = () => ({
    id: nextRowId(),
    act: "",
    titel: "",
    komponist: "",
    interpret_original: "",
    eigenes_werk: false,
    keine_musik: false,
    werkfassungsnummer: "",
    flags: [],
    alternative: null,
    search: null,
});

const rowFromEntry = (entry) => ({
    ...emptyRow(),
    act: entry.act || "",
    titel: entry.titel || "",
    komponist: entry.komponist || "",
    interpret_original: entry.interpret_original || "",
    eigenes_werk: Boolean(entry.eigenes_werk),
    keine_musik: Boolean(entry.keine_musik),
    werkfassungsnummer: entry.werkfassungsnummer || "",
});

const rowFromParsed = (parsed) => {
    const isBandName = parsed.flags.some((flag) => flag.code === "evtl_bandname");
    return {
        ...emptyRow(),
        act: parsed.act || "",
        titel: parsed.keine_musik ? (parsed.hinweis || "") : parsed.titel,
        // Bandname ist kein Komponist: dann nur als Interpret übernehmen.
        komponist: parsed.eigenes_werk ? parsed.act : (isBandName ? "" : parsed.kuenstler),
        interpret_original: parsed.eigenes_werk ? "" : parsed.kuenstler,
        eigenes_werk: parsed.eigenes_werk,
        keine_musik: parsed.keine_musik,
        flags: parsed.flags,
        alternative: parsed.alternative,
    };
};

const formatPersons = (persons) => (persons || [])
    .map((person) => `${person.name}${person.rolle ? ` (${person.rolle})` : ""}`)
    .join(", ");

const FlagList = ({ flags }) => {
    if (!flags || flags.length === 0) {
        return null;
    }
    return (
        <ul className="mt-1 space-y-0.5">
            {flags.map((flag, index) => (
                <li key={`${flag.code}_${index}`} className="text-[11px] text-amber-800">⚠ {flag.message}</li>
            ))}
        </ul>
    );
};

const GemaSetlistDialog = ({ open, onOpenChange, row, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [info, setInfo] = useState(null);
    const [auftritt, setAuftritt] = useState({});
    const [rows, setRows] = useState([]);
    const [importText, setImportText] = useState("");
    const [preview, setPreview] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    useEffect(() => {
        if (!open || !row?.event_id) {
            return;
        }
        let active = true;
        const load = async () => {
            setLoading(true);
            setPreview(null);
            setImportText("");
            setConfirmed(false);
            try {
                const data = await apiService.getAdminGemaSetlist(row.event_id, row.occurrence_id);
                if (!active) {
                    return;
                }
                setInfo(data);
                setAuftritt(data?.auftritt || {});
                const entries = Array.isArray(data?.entries) ? data.entries : [];
                setRows(entries.length > 0 ? entries.map(rowFromEntry) : []);
            } catch (error) {
                toast.error(error.response?.data?.detail || "Setlist konnte nicht geladen werden");
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [open, row]);

    const flaggedCount = useMemo(
        () => rows.filter((item) => item.flags.length > 0 || (item.search?.flags || []).length > 0).length,
        [rows],
    );

    const updateRow = (id, changes) => {
        setRows((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
    };

    const updateAuftritt = (key, value) => setAuftritt((prev) => ({ ...prev, [key]: value }));

    const handleParse = async () => {
        if (!importText.trim()) {
            return;
        }
        try {
            setParsing(true);
            const result = await apiService.parseGemaSetlistText(importText);
            setPreview(Array.isArray(result?.rows) ? result.rows : []);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Text konnte nicht gelesen werden");
        } finally {
            setParsing(false);
        }
    };

    const applyPreview = (mode) => {
        const parsedRows = (preview || []).map(rowFromParsed);
        setRows((prev) => (mode === "replace" ? parsedRows : [...prev, ...parsedRows]));
        setPreview(null);
        setImportText("");
        toast.success(`${parsedRows.length} Zeilen übernommen – markierte Zeilen bitte prüfen`);
    };

    const handleWerksuche = async () => {
        const candidates = rows.filter((item) => !item.keine_musik && !item.eigenes_werk && item.titel.trim());
        if (candidates.length === 0) {
            toast.info("Keine Zeilen für die Werksuche (eigene Werke und Programmpunkte ohne Musik werden übersprungen).");
            return;
        }
        try {
            setSearching(true);
            const result = await apiService.searchGemaWerke(candidates.map((item) => ({
                titel: item.titel,
                kuenstler: item.komponist || item.interpret_original || null,
                alternative: item.alternative,
            })));
            const results = Array.isArray(result?.results) ? result.results : [];
            setRows((prev) => prev.map((item) => {
                const index = candidates.findIndex((candidate) => candidate.id === item.id);
                if (index < 0) {
                    return item;
                }
                const search = results[index] || null;
                const suggestion = search?.suggestion;
                const changes = { search };
                if (suggestion && !item.werkfassungsnummer) {
                    changes.werkfassungsnummer = suggestion.werkfassungsnummer || "";
                }
                if (search?.order === "alternative") {
                    changes.titel = search.titel;
                    changes.komponist = search.kuenstler || "";
                    changes.interpret_original = "";
                }
                return { ...item, ...changes };
            }));
            const found = results.filter((item) => item?.suggestion).length;
            toast.success(`Werksuche: ${found} von ${candidates.length} sicher zugeordnet, Rest bitte auswählen oder manuell erfassen`);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Werksuche fehlgeschlagen");
        } finally {
            setSearching(false);
        }
    };

    const buildEntries = () => rows.map((item) => ({
        act: item.act.trim() || null,
        titel: item.titel.trim(),
        komponist: item.komponist.trim(),
        interpret_original: item.interpret_original.trim() || null,
        eigenes_werk: item.eigenes_werk,
        keine_musik: item.keine_musik,
        werkfassungsnummer: item.werkfassungsnummer || null,
    }));

    const handleSave = async () => {
        const incomplete = rows.filter((item) => !item.keine_musik && (!item.titel.trim() || (!item.komponist.trim() && !item.eigenes_werk)));
        if (incomplete.length > 0) {
            toast.error(`${incomplete.length} Zeile(n) ohne Titel oder Komponist – bitte ergänzen oder entfernen.`);
            return;
        }
        try {
            setSaving(true);
            const result = await apiService.updateAdminGemaSetlist(row.event_id, {
                occurrence_id: row.occurrence_id,
                entries: buildEntries(),
                auftritt: {
                    ...auftritt,
                    anzahl_musizierende: auftritt.anzahl_musizierende === "" || auftritt.anzahl_musizierende == null
                        ? null
                        : Number(auftritt.anzahl_musizierende),
                },
            });
            toast.success(`Setlist gespeichert (${result?.entries_count ?? 0} Einträge)`);
            if (onSaved) {
                onSaved();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Setlist konnte nicht gespeichert werden");
        } finally {
            setSaving(false);
        }
    };

    const submission = info?.setlist_submission;
    const hasProgrammverantwortlich = Boolean(
        String(auftritt.programmverantwortlich_nachname || "").trim()
        && String(auftritt.programmverantwortlich_vorname || "").trim(),
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Setlist für die GEMA</DialogTitle>
                    <DialogDescription>
                        {info?.title || row?.event_title || "Veranstaltung"}
                        {info?.lizenz_id ? ` – GEMA-Lizenz ${info.lizenz_id}${info.position ? ` / Pos. ${info.position}` : ""}` : ""}
                    </DialogDescription>
                </DialogHeader>

                {loading && <p className="text-sm text-gray-600">Wird geladen...</p>}

                {!loading && info && (
                    <div className="space-y-5">
                        {submission && (
                            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                                Setlist bei der GEMA eingereicht am {new Date(submission.submitted_at).toLocaleString("de-DE")}
                                {submission.submitted_by ? ` von ${submission.submitted_by}` : ""}
                                {submission.gema_gruppen_id ? ` (Gruppen-ID ${submission.gema_gruppen_id})` : ""}.
                            </div>
                        )}

                        <section className="space-y-3 rounded-lg border border-gray-200 p-4">
                            <p className="text-sm font-semibold text-gray-800">Interpret/-in</p>
                            {info.is_open_stage && (
                                <p className="text-xs text-indigo-800">
                                    Open Stage: mehrere gleichberechtigte Acts, programmverantwortlich ist die KuBu-Eventverantwortliche Person.
                                </p>
                            )}
                            <div className="grid gap-3 md:grid-cols-3">
                                <label className="text-xs font-semibold uppercase text-gray-500">
                                    Auftrittsart
                                    <select
                                        value={auftritt.auftrittsart || ""}
                                        onChange={(event) => updateAuftritt("auftrittsart", event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal normal-case text-gray-800"
                                    >
                                        {(info.auftrittsarten || []).map((art) => <option key={art} value={art}>{art}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs font-semibold uppercase text-gray-500">
                                    Name Interpret/-in
                                    <input type="text" value={auftritt.interpret_name || ""} onChange={(event) => updateAuftritt("interpret_name", event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal normal-case text-gray-800" />
                                </label>
                                <label className="text-xs font-semibold uppercase text-gray-500">
                                    Anzahl Musizierende
                                    <input type="number" min="0" value={auftritt.anzahl_musizierende ?? ""} onChange={(event) => updateAuftritt("anzahl_musizierende", event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal normal-case text-gray-800" />
                                </label>
                                <label className="text-xs font-semibold uppercase text-gray-500">
                                    Programmverantwortlich: Name *
                                    <input type="text" value={auftritt.programmverantwortlich_nachname || ""} onChange={(event) => updateAuftritt("programmverantwortlich_nachname", event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal normal-case text-gray-800" />
                                </label>
                                <label className="text-xs font-semibold uppercase text-gray-500">
                                    Vorname *
                                    <input type="text" value={auftritt.programmverantwortlich_vorname || ""} onChange={(event) => updateAuftritt("programmverantwortlich_vorname", event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal normal-case text-gray-800" />
                                </label>
                                <label className="text-xs font-semibold uppercase text-gray-500">
                                    E-Mail (optional)
                                    <input type="email" value={auftritt.programmverantwortlich_email || ""} onChange={(event) => updateAuftritt("programmverantwortlich_email", event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal normal-case text-gray-800" />
                                </label>
                            </div>
                        </section>

                        <section className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                            <p className="text-sm font-semibold text-indigo-900">Freitext-Import (z. B. aus Mail oder Teams)</p>
                            <textarea
                                value={importText}
                                onChange={(event) => setImportText(event.target.value)}
                                rows={6}
                                placeholder={"Act-Name\n- Titel - Künstler\n- Titel - Künstler\n\nNächster Act\n- ..."}
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs"
                            />
                            <button
                                type="button"
                                onClick={handleParse}
                                disabled={parsing || !importText.trim()}
                                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                            >
                                {parsing ? "Lese..." : "Text prüfen"}
                            </button>
                            {preview && (
                                <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
                                    <p className="text-xs font-semibold text-gray-700">
                                        Kontrolle: {preview.length} Zeilen erkannt, {preview.filter((item) => item.flags.length > 0).length} markiert. Nichts wurde korrigiert.
                                    </p>
                                    <div className="max-h-72 overflow-y-auto">
                                        <table className="min-w-full text-xs">
                                            <thead className="text-left text-gray-500">
                                                <tr><th className="px-2 py-1">Act</th><th className="px-2 py-1">Titel</th><th className="px-2 py-1">Künstler</th><th className="px-2 py-1">Hinweise</th></tr>
                                            </thead>
                                            <tbody>
                                                {preview.map((item) => (
                                                    <tr key={`preview_${item.line}`} className={item.flags.length > 0 ? "bg-amber-50" : ""}>
                                                        <td className="px-2 py-1 align-top">{item.act}</td>
                                                        <td className="px-2 py-1 align-top">{item.keine_musik ? <em>keine Musik</em> : item.titel}</td>
                                                        <td className="px-2 py-1 align-top">{item.kuenstler}</td>
                                                        <td className="px-2 py-1 align-top"><FlagList flags={item.flags} /></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button type="button" onClick={() => applyPreview("replace")} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">Übernehmen (ersetzen)</button>
                                        <button type="button" onClick={() => applyPreview("append")} className="rounded-lg border border-indigo-300 px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100">Anhängen</button>
                                        <button type="button" onClick={() => setPreview(null)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Verwerfen</button>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-semibold text-gray-800">
                                    Setlist ({rows.length} Zeilen{flaggedCount > 0 ? `, ${flaggedCount} zu prüfen` : ""})
                                </p>
                                <button type="button" onClick={() => setRows((prev) => [...prev, emptyRow()])} className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50">+ Zeile</button>
                                <button
                                    type="button"
                                    onClick={handleWerksuche}
                                    disabled={searching || rows.length === 0}
                                    className="ml-auto rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-60"
                                >
                                    {searching ? "Suche bei der GEMA..." : "GEMA-Werksuche"}
                                </button>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-gray-50 text-left text-gray-600">
                                        <tr>
                                            <th className="px-2 py-2">Act</th>
                                            <th className="px-2 py-2">Titel</th>
                                            <th className="px-2 py-2">Komponist/Autor</th>
                                            <th className="px-2 py-2">Interpret</th>
                                            <th className="px-2 py-2">Eigenes Werk</th>
                                            <th className="px-2 py-2">Keine Musik</th>
                                            <th className="px-2 py-2">GEMA-Werk</th>
                                            <th className="px-2 py-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((item) => {
                                            const searchFlags = item.search?.flags || [];
                                            const needsReview = item.flags.length > 0 || searchFlags.length > 0;
                                            return (
                                                <tr key={item.id} className={`border-t align-top ${needsReview ? "bg-amber-50/70" : ""}`}>
                                                    <td className="px-2 py-1"><input type="text" value={item.act} onChange={(event) => updateRow(item.id, { act: event.target.value })} className="w-28 rounded border border-gray-300 px-2 py-1" /></td>
                                                    <td className="px-2 py-1">
                                                        <input type="text" value={item.titel} onChange={(event) => updateRow(item.id, { titel: event.target.value, search: null })} placeholder={item.keine_musik ? "z. B. Comedy" : "Titel"} className="w-48 rounded border border-gray-300 px-2 py-1" />
                                                        <FlagList flags={[...item.flags, ...searchFlags]} />
                                                    </td>
                                                    <td className="px-2 py-1"><input type="text" value={item.komponist} disabled={item.keine_musik} onChange={(event) => updateRow(item.id, { komponist: event.target.value, search: null })} className="w-40 rounded border border-gray-300 px-2 py-1 disabled:bg-gray-100" /></td>
                                                    <td className="px-2 py-1"><input type="text" value={item.interpret_original} disabled={item.keine_musik} onChange={(event) => updateRow(item.id, { interpret_original: event.target.value })} className="w-32 rounded border border-gray-300 px-2 py-1 disabled:bg-gray-100" /></td>
                                                    <td className="px-2 py-1 text-center"><input type="checkbox" checked={item.eigenes_werk} disabled={item.keine_musik} onChange={(event) => updateRow(item.id, { eigenes_werk: event.target.checked, werkfassungsnummer: "" })} /></td>
                                                    <td className="px-2 py-1 text-center"><input type="checkbox" checked={item.keine_musik} onChange={(event) => updateRow(item.id, { keine_musik: event.target.checked, eigenes_werk: false, werkfassungsnummer: "" })} /></td>
                                                    <td className="px-2 py-1">
                                                        {item.keine_musik || item.eigenes_werk ? (
                                                            <span className="text-gray-500">{item.keine_musik ? "kein Werk" : "manuell (eigenes Werk)"}</span>
                                                        ) : item.search?.error ? (
                                                            <span className="text-red-700">{item.search.error}</span>
                                                        ) : item.search ? (
                                                            <div className="space-y-1">
                                                                {(item.search.hits || []).map((hit) => (
                                                                    <label key={`${item.id}_${hit.werkfassungsnummer || hit.titel}`} className="flex items-start gap-1">
                                                                        <input
                                                                            type="radio"
                                                                            name={`werk_${item.id}`}
                                                                            checked={item.werkfassungsnummer === (hit.werkfassungsnummer || "")}
                                                                            onChange={() => updateRow(item.id, { werkfassungsnummer: hit.werkfassungsnummer || "" })}
                                                                            className="mt-0.5"
                                                                        />
                                                                        <span>
                                                                            <span className="font-semibold">{hit.titel}</span>
                                                                            {item.search.suggestion?.werkfassungsnummer === hit.werkfassungsnummer && <span className="ml-1 rounded bg-green-100 px-1 text-green-800">Vorschlag</span>}
                                                                            <br />
                                                                            <span className="text-gray-600">{formatPersons(hit.beteiligte)} · {hit.werkfassungsnummer || "-"}</span>
                                                                        </span>
                                                                    </label>
                                                                ))}
                                                                <label className="flex items-center gap-1">
                                                                    <input type="radio" name={`werk_${item.id}`} checked={!item.werkfassungsnummer} onChange={() => updateRow(item.id, { werkfassungsnummer: "" })} />
                                                                    Manuell erfassen
                                                                </label>
                                                            </div>
                                                        ) : item.werkfassungsnummer ? (
                                                            <span>{item.werkfassungsnummer}</span>
                                                        ) : (
                                                            <span className="text-gray-400">noch nicht gesucht</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1"><button type="button" onClick={() => setRows((prev) => prev.filter((entry) => entry.id !== item.id))} className="text-red-600 hover:underline">Entfernen</button></td>
                                                </tr>
                                            );
                                        })}
                                        {rows.length === 0 && (
                                            <tr><td colSpan={8} className="px-2 py-4 text-center text-gray-500">Noch keine Einträge. Text importieren oder Zeilen hinzufügen.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <section className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            {!GEMA_SETLIST_SUBMIT_ENABLED && (
                                <p>
                                    <span className="font-semibold">Einreichen aus dem KuBu-Tool ist noch gesperrt</span>, bis die Portal-Requests mitgeschnitten sind.
                                    Bis dahin die geprüfte Setlist speichern und im{" "}
                                    <a href={GEMA_PORTAL_URL} target="_blank" rel="noreferrer" className="font-semibold underline">GEMA-Portal</a>{" "}
                                    unter „Setlist einreichen“ übertragen. Korrektur ist dort nur 1 Tag lang möglich.
                                </p>
                            )}
                            {!hasProgrammverantwortlich && <p>Programmverantwortliche:r (Name, Vorname) fehlt noch.</p>}
                            <label className="flex items-start gap-2">
                                <input type="checkbox" checked={confirmed} disabled={!GEMA_SETLIST_SUBMIT_ENABLED} onChange={(event) => setConfirmed(event.target.checked)} className="mt-1" />
                                Ich habe die Setlist geprüft und reiche sie verbindlich bei der GEMA ein.
                            </label>
                        </section>
                    </div>
                )}

                <DialogFooter>
                    <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Schließen</button>
                    <button type="button" onClick={handleSave} disabled={saving || loading} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                        {saving ? "Speichern..." : "Im KuBu-Tool speichern"}
                    </button>
                    <button
                        type="button"
                        disabled={!GEMA_SETLIST_SUBMIT_ENABLED || !confirmed || !hasProgrammverantwortlich}
                        title={GEMA_SETLIST_SUBMIT_ENABLED ? undefined : "Gesperrt, bis der Portal-Request mitgeschnitten ist"}
                        className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60"
                    >
                        Setlist einreichen{GEMA_SETLIST_SUBMIT_ENABLED ? "" : " (gesperrt)"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default GemaSetlistDialog;
